import { getReferrerAttribution } from "@outschool/attribution";
import { perStudentPriceInCents } from "@outschool/business-rules";
import {
  CouponState,
  CurrencyCode,
  EnrollmentInput,
  Period
} from "@outschool/gql-backend-generated";
import {
  ActivityEnrollQueryQuery,
  CharterAdminEnrollmentInput,
  EnrollmentMeetingPurchaseQueryQuery,
  GetLearnerItems_EnrollmentFragment,
  PricingBreakdownQueryQuery
} from "@outschool/gql-frontend-generated";
import { useLocalStorageState } from "@outschool/local-storage";
import * as Localization from "@outschool/localization";
import { USD_STATIC_EXCHANGE_RATE } from "@outschool/localization";
import { plural } from "@outschool/text";
import { dayjs } from "@outschool/time";
import { useSession } from "@outschool/ui-auth";
import Cookies from "js-cookie";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import React from "react";

import * as Enrollment from "../../../shared/Enrollment";
import { usePricingBreakdown } from "../../hooks/Order";
import { shouldChargeInLocalCurrency } from "../../lib/Localization";
import { getLearnerItems } from "./enrollmentForm/learnerItems/getLearnerItems";
import {
  LearnerItemType,
  getBuyingLearnerItems
} from "./enrollmentForm/learnerItems/LearnerItemCheckboxes";
import { MeetingDaySelectOption } from "./enrollmentForm/OngoingClassMeetingInputs";

type PrepaidOngoingPeriodOptions = {
  startOfPeriodOptions: MeetingDaySelectOption[];
  endOfPeriodOptions: MeetingDaySelectOption[];
};

//MARK: Order Context
export const OrderContext = React.createContext<{
  buyingLearners: LearnerItemType["learner"][];
  currencyCode: CurrencyCode;
  exchangeRate: number;
  isChargingInLocalCurrency: boolean;
  isUsingPurchaseOrder?: boolean;
  prepaidOngoingPeriodOptions?: PrepaidOngoingPeriodOptions;
  hasEsaMembership?: boolean;
  isPrepaidOngoingEnrollment?: boolean;
  setIsPrepaidOngoingEnrollment?: React.Dispatch<React.SetStateAction<boolean>>;

  // Pricing breakdown
  isPricingBreakdownLoading: boolean;
  pricingBreakdown?: PricingBreakdownQueryQuery["pricingBreakdown"];
  pricingBreakdownError?: string;
  refetchPricingBreakdown: () => Promise<void>;

  // State management
  enrollmentInputs: EnrollmentInput[];
  setEnrollmentInputs(enrollments: EnrollmentInput[]): void;
  learnerItems: LearnerItemType[];
  setLearnerItems(learners: LearnerItemType[]): void;
  setOverrideLearnerItems(learners: LearnerItemType[]): void;
  donationAmountCents: number;
  setDonationAmountCents(cents: number): void;
  promoCode?: string;
  setPromoCode(code?: string): void;
  isWeeklyPayment: boolean;
  setIsWeeklyPayment(bool: boolean): void;
  ongoingStartTime?: Date | null;
  setOngoingStartTime: React.Dispatch<React.SetStateAction<Date | null>>;
  ongoingEndTime?: Date | null;
  setOngoingEndTime?: React.Dispatch<React.SetStateAction<Date | null>>;
  charterAdminEnrollmentInput?: CharterAdminEnrollmentInput | null;
  setCharterAdminEnrollmentInput: React.Dispatch<
    React.SetStateAction<CharterAdminEnrollmentInput | null>
  >;
  prepayDurationWeeks?: number | null;
  setPrepayDurationWeeks: React.Dispatch<React.SetStateAction<number>>;
  setTeacherUid?: React.Dispatch<React.SetStateAction<string>>;
}>({
  buyingLearners: [],
  currencyCode: Localization.BASE_CURRENCY.code,
  exchangeRate: Localization.USD_STATIC_EXCHANGE_RATE,
  isChargingInLocalCurrency: false,
  isUsingPurchaseOrder: false,
  prepaidOngoingPeriodOptions: {
    startOfPeriodOptions: [],
    endOfPeriodOptions: []
  },

  // Pricing breakdown
  isPricingBreakdownLoading: false,
  refetchPricingBreakdown: async () => undefined,

  // State management
  enrollmentInputs: [],
  setEnrollmentInputs: () => null,
  learnerItems: [],
  setLearnerItems: () => null,
  setOverrideLearnerItems: () => null,
  donationAmountCents: 0,
  setDonationAmountCents: () => null,
  promoCode: undefined,
  setPromoCode: (_v?: string) => null,
  isWeeklyPayment: false,
  setIsWeeklyPayment: () => null,
  ongoingStartTime: null,
  setOngoingStartTime: () => null,
  ongoingEndTime: null,
  setOngoingEndTime: () => null,
  charterAdminEnrollmentInput: undefined,
  setCharterAdminEnrollmentInput: () => null,
  prepayDurationWeeks: 1,
  setPrepayDurationWeeks: () => null,
  setTeacherUid: () => null
});

type OrderProviderProps = {
  children: React.ReactNode;
  buyingUserUid: string;
  activity:
    | NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["activity"]
    | NonNullable<
        EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]
      >["activity"];
  section?:
    | NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["section"]
    | null;
  lastSubmittedEnrollments?: GetLearnerItems_EnrollmentFragment[];
  enrollingParent: ActivityEnrollQueryQuery["currentUser"] | null;
  enrollingParentEnrollments?: GetLearnerItems_EnrollmentFragment[];
  enrollmentsInOtherSections?: GetLearnerItems_EnrollmentFragment[];
  conflictingEnrollments?: GetLearnerItems_EnrollmentFragment[];
  pendingEnrollments?: GetLearnerItems_EnrollmentFragment[];
  isUsingPurchaseOrder?: boolean;
  purchaseOrderCoverage?: Date | null;
  isAcceptingGiftEnrollment?: boolean;
  currencyCode?: CurrencyCode;
  exchangeRate?: number;
  ignoreStrictAgeRange: boolean;
  sectionPrepaidOngoingPeriodOptions?: Period[] | null;
  numMeetings?: number;
  hasEsaMembership?: boolean;
};

function enrollmentInputsForLearners(
  priceCents: number,
  buyingLearners: LearnerItemType["learner"][]
): EnrollmentInput[] {
  return buyingLearners.map(learner =>
    Enrollment.enrollmentInputFromLearner(
      {
        uid: learner.uid,
        name: learner.name,
        age: learner.age,
        date_of_birth: learner.date_of_birth
      },
      priceCents,
      undefined
    )
  );
}

export const generatePrepaidOngoingPeriodOptions = (
  sectionPrepaidOngoingPeriodOptions?: Period[] | null
) => {
  const startOfPeriodOptions: MeetingDaySelectOption[] = [];
  const endOfPeriodOptions: MeetingDaySelectOption[] = [];

  sectionPrepaidOngoingPeriodOptions?.forEach(
    ({ startOfIsoWeek, endOfIsoWeek }, idx) => {
      const startOfPeriodOption: MeetingDaySelectOption = {
        label: `Starts ${dayjs(startOfIsoWeek).format("ddd M/D")}`,
        value: startOfIsoWeek
      };

      const endOfPeriodOption: MeetingDaySelectOption = {
        label: `${idx + 1} ${plural("Week", idx + 1)} - Ending ${dayjs(
          endOfIsoWeek
        ).format("ddd M/D")}`,
        value: endOfIsoWeek
      };

      startOfPeriodOptions.push(startOfPeriodOption);
      endOfPeriodOptions.push(endOfPeriodOption);
    }
  );

  return {
    startOfPeriodOptions,
    endOfPeriodOptions
  };
};

const AUTO_APPLY_FAILED_COUPON_CODE_LS_KEY = "auto-apply-coupon-code-failed";

// MARK: Order Provider
export const OrderProvider = ({
  children,
  buyingUserUid,
  activity,
  enrollingParent,
  lastSubmittedEnrollments,
  enrollmentsInOtherSections,
  enrollingParentEnrollments,
  conflictingEnrollments,
  pendingEnrollments,
  isUsingPurchaseOrder,
  purchaseOrderCoverage,
  isAcceptingGiftEnrollment,
  currencyCode,
  exchangeRate,
  ignoreStrictAgeRange,
  sectionPrepaidOngoingPeriodOptions = [],
  section,
  hasEsaMembership
}: OrderProviderProps) => {
  const { isEsaSession } = useSession();
  const [donationAmountCents, setDonationAmountCents] =
    React.useState<number>(0);
  const [enrollmentInputs, setEnrollmentInputs] = React.useState<
    EnrollmentInput[]
  >([]);
  const [autoApplyFailedCouponCode, setAutoApplyFailedCouponCode] =
    useLocalStorageState<string | undefined>(
      AUTO_APPLY_FAILED_COUPON_CODE_LS_KEY,
      undefined
    );
  const autoApplyCoupon =
    Cookies.get("os-apply-coupon") || getReferrerAttribution()?.couponCode;
  const [promoCode, setPromoCode] = React.useState<string | undefined>(
    autoApplyFailedCouponCode !== autoApplyCoupon ? autoApplyCoupon : ""
  );
  const [overrideLearnerItems, setOverrideLearnerItems] = React.useState<
    LearnerItemType[]
  >([]);
  const onlyAllowSingleLearner =
    isEsaSession ||
    activity.hasTeacherSchedule ||
    activity.isSelfPaced ||
    !!hasEsaMembership;
  const [learnerItems, setLearnerItems] = React.useState<LearnerItemType[]>(
    getLearnerItems({
      activity,
      enrollingParent,
      lastSubmittedEnrollments,
      enrollmentsInOtherSections,
      enrollingParentEnrollments,
      conflictingEnrollments,
      pendingEnrollments,
      onlyAllowSingleLearner,
      ignoreStrictAgeRange
    })
  );

  const isChargingInLocalCurrency =
    !!currencyCode && shouldChargeInLocalCurrency(currencyCode);
  const [isWeeklyPayment, setIsWeeklyPayment] = React.useState<boolean>(false);
  const buyingLearners = React.useMemo(
    () =>
      getBuyingLearnerItems(
        overrideLearnerItems.length > 0 ? overrideLearnerItems : learnerItems
      ),
    [overrideLearnerItems, learnerItems]
  );

  /* ongoing class meeting period options */
  const [ongoingStartTime, setOngoingStartTime] = React.useState<Date | null>(
    null
  );
  const [ongoingEndTime, setOngoingEndTime] = React.useState<Date | null>(null);
  const [isPrepaidOngoingEnrollment, setIsPrepaidOngoingEnrollment] =
    React.useState<boolean>(false);

  const [teacherUid, setTeacherUid] = React.useState<string | null>(null);
  const [charterAdminEnrollmentInput, setCharterAdminEnrollmentInput] =
    React.useState<CharterAdminEnrollmentInput | null>(null);
  const [prepayDurationWeeks, setPrepayDurationWeeks] = React.useState<
    number | null
  >(null);

  const {
    pricingBreakdown,
    loading: isPricingBreakdownLoading,
    refetch,
    error
  } = usePricingBreakdown({
    activityUid: activity.uid,
    isUsingPurchaseOrder,
    purchaseOrderCoverage,
    sectionUid: section?.uid,
    buyingUserUid,
    buyingLearnerUids: buyingLearners.map(
      buyingLearner => buyingLearner.uid ?? null
    ),
    enrollmentCount: enrollmentInputs.length,
    donationCents: donationAmountCents,
    isAcceptingGiftEnrollment,
    couponCode: promoCode,
    ongoingStartTime,
    ongoingEndTime,
    isWeeklyPayment,
    teacherUid,
    prepayDurationWeeks,
    charterAdminEnrollmentInput,
    isPrepaidOngoingEnrollment
  });

  // Store any failed coupon code auto-applications in local storage so that
  // we don't keep trying to auto-apply the coupon code
  React.useEffect(() => {
    // has the pricing breakdown loaded and did the coupon application fail?
    const didCouponCodeApplyFail =
      !isPricingBreakdownLoading &&
      pricingBreakdown?.couponState !== CouponState.Valid;
    // did the coupon code come from the URL query string or cookie
    const wasCouponCodeAutoApplied =
      !!autoApplyCoupon && autoApplyCoupon === promoCode;
    // have we already set localstorage with this auto-applied coupon code value?
    const isAutoApplyFailedCouponCodeNotAlreadySet =
      autoApplyCoupon !== autoApplyFailedCouponCode;
    if (
      didCouponCodeApplyFail &&
      wasCouponCodeAutoApplied &&
      isAutoApplyFailedCouponCodeNotAlreadySet
    ) {
      setAutoApplyFailedCouponCode(autoApplyCoupon);
    }
  }, [
    pricingBreakdown?.couponState,
    isPricingBreakdownLoading,
    autoApplyFailedCouponCode,
    setAutoApplyFailedCouponCode,
    promoCode,
    autoApplyCoupon
  ]);

  React.useEffect(() => {
    const enrollmentPrice = {
      price_cents: pricingBreakdown?.enrollmentMeetingTotalCents
    };

    const useProratedPrice =
      pricingBreakdown?.hasProratedEnrollments &&
      pricingBreakdown?.perEnrollmentUpfrontCents;
    const priceCents = activity?.hasTeacherSchedule
      ? perStudentPriceInCents(activity, null, enrollmentPrice)
      : useProratedPrice
      ? pricingBreakdown.perEnrollmentUpfrontCents!
      : perStudentPriceInCents(activity, section);

    setEnrollmentInputs(
      enrollmentInputsForLearners(priceCents, buyingLearners)
    );
  }, [
    activity,
    pricingBreakdown,
    section,
    buyingLearners,
    currencyCode,
    exchangeRate,
    isChargingInLocalCurrency
  ]);

  const isWeeklyPaymentEligible = React.useMemo(() => {
    return (
      activity.allows_recurring_payment &&
      !activity.is_ongoing_weekly &&
      !activity.isClub &&
      !!activity.duration_weeks &&
      activity.duration_weeks > 1
    );
  }, [
    activity.allows_recurring_payment,
    activity.is_ongoing_weekly,
    activity.isClub,
    activity.duration_weeks
  ]);

  const isOngoing = !!activity.is_ongoing_weekly;
  const prepaidOngoingPeriodOptions: PrepaidOngoingPeriodOptions =
    React.useMemo(() => {
      if (!isOngoing && !isWeeklyPaymentEligible) {
        return {
          startOfPeriodOptions: [],
          endOfPeriodOptions: []
        };
      }
      return generatePrepaidOngoingPeriodOptions(
        sectionPrepaidOngoingPeriodOptions
      );
    }, [
      sectionPrepaidOngoingPeriodOptions,
      isOngoing,
      isWeeklyPaymentEligible
    ]);

  React.useEffect(() => {
    setLearnerItems(
      overrideLearnerItems.length > 0
        ? overrideLearnerItems
        : getLearnerItems({
            activity,
            enrollingParent,
            lastSubmittedEnrollments,
            enrollingParentEnrollments,
            enrollmentsInOtherSections,
            conflictingEnrollments,
            pendingEnrollments,
            onlyAllowSingleLearner,
            ignoreStrictAgeRange
          })
    );
    // TODO: this should be updated to be able to use the actual args to getLearnerItems, however they seem to change upstream
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollingParent?.uid, overrideLearnerItems]);
  return (
    <OrderContext.Provider
      value={{
        buyingLearners,
        currencyCode: currencyCode ?? CurrencyCode.Usd,
        exchangeRate: exchangeRate ?? USD_STATIC_EXCHANGE_RATE,
        isChargingInLocalCurrency,
        isUsingPurchaseOrder,
        isPrepaidOngoingEnrollment,
        prepaidOngoingPeriodOptions,
        hasEsaMembership,

        // Pricing breakdown
        isPricingBreakdownLoading,
        pricingBreakdown,
        pricingBreakdownError: error?.message,
        refetchPricingBreakdown: refetch,

        // State management
        enrollmentInputs,
        setEnrollmentInputs,
        learnerItems,
        setLearnerItems,
        setOverrideLearnerItems,
        donationAmountCents,
        setDonationAmountCents,
        promoCode,
        setPromoCode,
        isWeeklyPayment,
        setIsWeeklyPayment,
        ongoingStartTime,
        setOngoingStartTime,
        ongoingEndTime,
        setOngoingEndTime,
        charterAdminEnrollmentInput,
        setCharterAdminEnrollmentInput,
        prepayDurationWeeks,
        setPrepayDurationWeeks,
        setTeacherUid,
        setIsPrepaidOngoingEnrollment
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export function useOrderContext() {
  return React.useContext(OrderContext);
}
